import * as React from "react";
import LazyLoad from "@vingle/wtf-sdk/components/lazyLoad";
import { useParentScrollElement } from "@vingle/wtf-sdk/helpers/hook";
import BlurhashCanvas from "common/components/blurHash";
import { isBrowser } from "@vingle/wtf-sdk/helpers/envChecker";
import { Wrapper, Media, BlurHash as BlurHashStyle } from "./styledComponents";
import {
  videoLoad,
  imageLoad,
  videoLoadElement,
  imageLoadElement,
} from "./helpers";

const BlurHash = BlurHashStyle.withComponent(BlurhashCanvas);

const MAX_WAITING_VIDEO_TIME = 5000;

interface IProps
  extends React.PropsWithChildren<{
      src: string;
      type: "image" | "video";
      width: number;
      height: number;
      srcSet?: string;
      previewColor?: string;
      blurHash?: string;
      onSuccess?: VoidFunction;
    }>,
    React.HTMLAttributes<HTMLDivElement> {}

async function loadMediaElement({
  wrapperRef,
  type,
  src,
  srcSet,
}: {
  wrapperRef: React.RefObject<HTMLElement>;
  type: string;
  src: string;
  srcSet?: string;
}) {
  switch (type) {
    case "image": {
      const imageElement = wrapperRef.current?.querySelector("img");
      if (imageElement) {
        await imageLoadElement(imageElement);
      } else {
        await imageLoad(src, srcSet);
      }
      break;
    }
    case "video": {
      await Promise.race([
        (async () => {
          const videoElement = wrapperRef.current?.querySelector("video");
          if (videoElement) {
            await videoLoadElement(videoElement);
          } else {
            await videoLoad(src);
          }
        })(),
        new Promise(resolve => setTimeout(resolve, MAX_WAITING_VIDEO_TIME)),
      ]);
      break;
    }
  }
}

function useMediaLazyTransition({ type, src, srcSet, onSuccess }: IProps) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const scrollElement = useParentScrollElement(wrapperRef);
  const [isVisible, setVisible] = React.useState(false);
  const [isLoaded, setLoaded] = React.useState(!isBrowser());
  const handleVisible = React.useCallback(async () => {
    if (!isLoaded) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      onSuccess?.();
    }
  }, [isLoaded]);
  React.useLayoutEffect(() => {
    if (isVisible) {
      (async () => {
        await loadMediaElement({
          wrapperRef,
          type,
          src,
          srcSet,
        });
        requestAnimationFrame(() => setLoaded(true));
        onSuccess?.();
      })();
    }
  }, [isVisible]);
  return {
    wrapperRef,
    scrollElement,
    isLoaded,
    isVisible,
    handleVisible,
  };
}

function MediaLazyTransitionWrapper(props: IProps) {
  const {
    children,
    blurHash,
    previewColor,
    width,
    height,
    src,
    type,
    style,
    onSuccess,
    ...rest
  } = props;
  const {
    wrapperRef,
    scrollElement,
    isLoaded,
    isVisible,
    handleVisible,
  } = useMediaLazyTransition(props);
  return (
    <Wrapper
      ref={wrapperRef}
      style={{
        ...style,
        paddingTop: `${(height / width) * 100}%`,
      }}
      {...rest}
    >
      <LazyLoad root={scrollElement} onVisible={handleVisible}>
        <Media transition={isVisible} style={{ opacity: Number(isLoaded) }}>
          {children}
        </Media>
      </LazyLoad>
      {(previewColor || blurHash) && (
        <BlurHash
          className="placeholder"
          hash={blurHash}
          width={width}
          height={height}
          style={{
            backgroundColor: previewColor,
          }}
        />
      )}
    </Wrapper>
  );
}

export default MediaLazyTransitionWrapper;
